import { is } from 'ramda';

/**
 * Butter template literal utility
 *
 * butter(str, values) returns `str` with occurrences of `${var}` replaced with values of `var` looked up in `values`
 *
 * For example
 *
 * butter("Sentence with ${value} and ${text} interpolation", {value: 17, text: "some"})
 *
 * -> "Sentence with 17 and some interpolation".
 */
export const interpolate = (str: string, values: any) => {
	// Replace {(whitespace)(key)(whitespace)} not preceded by \
	// If key unmatched, leave alone
	// 'http[s]://' before the {} is stripped in order to accommodate butter behavior wrt external vs internal links
	return str.replace(
		/(http:\/\/|https:\/\/|\\)?\{\s*([^{}}\s]+)\s*\}/g,
		(match: string, before: string, key: string) =>
			before == '\\'
				? match
				: values[key] === undefined
				? match
				: before == 'http://' || before == 'https://'
				? `${values[key]}`
				: `${before ?? ''}${values[key]}`
	);
};

// Code adapted from https://matthiashager.com/converting-snake-case-to-camel-case-object-keys-with-javascript
const isObject = (o: unknown) => {
	return o === Object(o) && !isArray(o) && typeof o !== 'function';
};

const isArray = (a: unknown) => {
	return Array.isArray(a);
};

export const toCamel = (s: string) => {
	return s.replace(/([-_][a-z0-9])/gi, ($1) => {
		return $1.toUpperCase().replace('-', '').replace('_', '');
	});
};

type KeysToCamel = (record: unknown) => Record<string, unknown> | string[] | unknown;
export const keysToCamel: KeysToCamel = (record) => {
	if (isObject(record)) {
		const newRecord: Record<string, unknown> = {};

		Object.keys(record as Record<string, unknown>).forEach((key: string) => {
			newRecord[toCamel(key)] = keysToCamel((record as Record<string, unknown>)[key]);
		});

		return newRecord;
	} else if (isArray(record)) {
		return (record as string[]).map((value: string) => {
			return keysToCamel(value);
		});
	}

	return record;
};

export const toSnake = (s: string) => {
	return s.replace(/([A-Z])/g, ($1) => {
		return $1.replace($1, `_${$1}`);
	});
};

type CamelToSnake = (record: unknown) => Record<string, unknown> | string[] | string | unknown;
export const camelToSnake: CamelToSnake = (record) => {
	if (isObject(record)) {
		const newRecord: Record<string, unknown> = {};

		Object.keys(record as Record<string, unknown>).forEach((key: string) => {
			newRecord[toSnake(key)] = camelToSnake((record as Record<string, unknown>)[key]);
		});

		return newRecord;
	} else if (isArray(record)) {
		return (record as string[]).map((value: string) => {
			return camelToSnake(value);
		});
	}

	return record;
};

export const toKebab = (s: string) => {
	return s.replace(/([A-Z])/g, ($1) => {
		return $1.replace($1, `-${$1}`);
	});
};

type CamelToKebab = (record: unknown) => Record<string, unknown> | string[] | string | unknown;
export const camelToKebab: CamelToKebab = (record) => {
	if (isObject(record)) {
		const newRecord: Record<string, unknown> = {};

		Object.keys(record as Record<string, unknown>).forEach((key: string) => {
			newRecord[toKebab(key)] = camelToKebab((record as Record<string, unknown>)[key]);
		});

		return newRecord;
	} else if (isArray(record)) {
		return (record as string[]).map((value: string) => {
			return camelToKebab(value);
		});
	}

	return record;
};
